import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { AuthProvider } from './contexts/AuthContext';

import Home from './views/Home';
import CraftiesMarketplace from './views/CraftiesMarketplace';
import TradesDirectory from './views/TradesDirectory';
import TraderRegistration from './views/TraderRegistration';
import CraftsmanRegistration from './views/CraftsmanRegistration';
import ProfessionalRegistration from './views/ProfessionalRegistration';
import EmailConfirmation from './views/EmailConfirmation';
import PrivateRoute from './components/auth/PrivateRoute';
import Navbar from './components/Navbar';
import AdminLayout from './views/admin/AdminLayout';
import AdminLogin from './views/admin/AdminLogin';
import Dashboard from './views/admin/Dashboard';
import Categories from './views/admin/Categories';
import ProfessionManager from './views/admin/ProfessionManager';
import Traders from './views/admin/Traders';
import Crafties from './views/admin/Crafties';
import Hospitality from './views/admin/Hospitality';
import Professionals from './views/admin/Professionals';
import Approvals from './views/admin/Approvals';
import AddBusiness from './views/admin/AddBusiness';
import RegisterUser from './views/admin/RegisterUser';
import ChangePassword from './views/admin/ChangePassword';
import ImportBusinesses from './views/admin/ImportBusinesses';
import TraderDashboard from './views/trader/TraderDashboard';
import TraderLayout from './views/trader/TraderLayout';
import CraftieDashboard from './views/craftie/CraftieDashboard';
import UserDashboard from './views/UserDashboard';
import TraderDetailPage from './views/TraderDetailPage';
import CraftieDetailPage from './views/CraftieDetailPage';
import HospitalityDetailPage from './views/HospitalityDetailPage';
import ProfessionalDetailPage from './views/ProfessionalDetailPage';
import SearchResults from './views/SearchResults';
import SearchTest from './components/SearchTest';
import Login from './views/Login';
import UserRegistration from './views/UserRegistration';
import ClaimBusiness from './views/ClaimBusiness';
import ClaimBusinessCTA from './views/ClaimBusinessCTA';
import ClaimVerify from './views/ClaimVerify';

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    try {
      // Add a small delay to ensure styles are loaded
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 100);

      return () => clearTimeout(timer);
    } catch (err) {
      console.error('App initialization error:', err);
      setError(err as Error);
    }
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Application Error</h2>
          <p className="text-gray-600 mb-4">
            We're sorry, but there was an error loading the application.
          </p>
          <p className="text-sm text-gray-500 mb-4">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AuthProvider>
        <AdminAuthProvider>
          <div className={`fade-in ${isVisible ? 'visible' : ''}`}>
            <Routes>
              {/* Public routes */}
              <Route element={<>
                <Navbar />
                <Outlet />
              </>}>
                <Route path="/" element={<Home />} />
                <Route path="/crafties" element={<CraftiesMarketplace />} />
                <Route path="/trades" element={<TradesDirectory />} />
                <Route path="/register/trader" element={<TraderRegistration />} />
                <Route path="/register/business" element={<CraftsmanRegistration />} />
                <Route path="/register/professional" element={<ProfessionalRegistration />} />
                <Route path="/register/user" element={<UserRegistration />} />
                <Route path="/email-confirmation" element={<EmailConfirmation />} />
                <Route path="/confirm-email" element={<EmailConfirmation />} />
                <Route path="/search" element={<SearchResults />} />
                <Route path="/search-test" element={<SearchTest />} />
                <Route path="/trader/:slug" element={<TraderDetailPage />} />
                <Route path="/craftsman/:slug" element={<CraftieDetailPage />} />
                <Route path="/hospitality/:slug" element={<HospitalityDetailPage />} />
                <Route path="/professional/:slug" element={<ProfessionalDetailPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/claim/:traderId" element={<ClaimBusinessCTA />} />
                <Route path="/claim-business/:traderId" element={<ClaimBusiness />} />
                <Route path="/claim-verify" element={<ClaimVerify />} />
              </Route>

              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route element={
                <PrivateRoute requiredRole="admin">
                  <SidebarProvider>
                    <AdminLayout />
                  </SidebarProvider>
                </PrivateRoute>
              }>
                <Route path="/admin" element={<Dashboard />} />
                <Route path="/admin/categories" element={<Categories />} />
                <Route path="/admin/traders" element={<Traders />} />
                <Route path="/admin/crafties" element={<Crafties />} />
                <Route path="/admin/hospitality" element={<Hospitality />} />
                <Route path="/admin/professionals" element={<Professionals />} />
                <Route path="/admin/approvals" element={<Approvals />} />
                <Route path="/admin/add-business" element={<AddBusiness />} />
                <Route path="/admin/register-user" element={<RegisterUser />} />
                <Route path="/admin/import-businesses" element={<ImportBusinesses />} />
                <Route path="/admin/professions" element={<ProfessionManager />} />
                <Route path="/admin/change-password" element={<ChangePassword />} />
              </Route>

              {/* Generic protected user dashboard (non-admin) */}
              <Route element={
                <PrivateRoute>
                  <Outlet />
                </PrivateRoute>
              }>
                <Route path="/dashboard" element={<UserDashboard />} />
              </Route>

              {/* Protected trader routes */}
              <Route element={
                <PrivateRoute requiredRole="trader">
                  <TraderLayout />
                </PrivateRoute>
              }>
                <Route path="/trader/dashboard" element={<TraderDashboard />} />
              </Route>

              {/* Protected craftie routes */}
              <Route element={
                <PrivateRoute requiredRole="craftsman">
                  <TraderLayout />
                </PrivateRoute>
              }>
                <Route path="/craftie/dashboard" element={<CraftieDashboard />} />
              </Route>

              {/* Catch all route */}
              <Route path="*" element={
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">404 - Page Not Found</h2>
                    <p className="text-gray-600">The page you're looking for doesn't exist.</p>
                  </div>
                </div>
              } />
            </Routes>
          </div>
        </AdminAuthProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;